import { Plus, Warning } from 'phosphor-react'
import { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import DashboardLayout from '../../../../layouts/Dashboard'
import api from '../../../../services/api'
import { formatDateTSX } from '../../../../utils/tsxFormat'
import { formatMoney } from '../../../../utils/format'

import styles from './styles.module.css'
import VehicleCard from '../../../../components/VehicleCard'
import { useLoading } from '../../../../hooks/LoadingContext'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../../components/SearchInput'

interface ITicket {
  id: number
  type: string
  vehicle_board: string
  vehicle_model: string
  vehicle_color: string
  driver_name: string
  description: string
  date: Date
  cost: number
  points: number
}

interface ITicketCard {
  Motorista: string
  Custo: number
  Pontos: number
}

const Multas: React.FC = () => {
  const [search, setSearch] = useState<string>('')
  const [lastSearch, setLastSearch] = useState<string>('')
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const { isLoading, setIsLoading } = useLoading()
  const [tickets, setTickets] = useState<ITicket[]>()
  const [ticket, setTicket] = useState<ITicket>()
  const [showTicket, setShowTicket] = useState<boolean>(false)

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/delivery-manager/traffic-tickets?limit=20'
    if (search) apiUrl += '&search=' + search

    const offset = tickets && !isSearchDifferent ? tickets.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setTickets(data)
        setHasMoreData(false)
      } else {
        if (!tickets) setTickets(data)
        else setTickets(isSearchDifferent ? data : tickets.concat(data))
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tickets, hasMoreData, setIsLoading])

  useEffect(() => {
    if (!tickets && hasMoreData) loadData()
  }, [loadData, hasMoreData, tickets])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  const navigate = useNavigate()

  const getCardData = (data: ITicket) => {
    let newData: ITicketCard = {
      Motorista: data.driver_name,
      Custo: data.cost,
      Pontos: data.points,
    }

    return newData
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Multas">
      <LoadingSpinner text="Buscando multas. Aguarde..." />
      <Row style={{ marginInline: 30 }}>
        <SearchInput
          placeholder="Digite a placa do veículo ou o nome do motorista"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', marginInline: 30 }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={`${styles.newBtn} px-3 px-md-5`}
            onClick={() => navigate('/gestor-entregas/veiculos/multas/nova')}
          >
            <Plus
              style={{ marginBottom: 4, marginRight: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Nova multa
          </Button>
        </Col>
      </Row>
      <Row
        style={{ marginInline: 30, display: 'flex' }}
        className="justify-content-center justify-content-md-start"
      >
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!tickets?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhuma
            multa encontrada!
          </Badge>
        )}
        {tickets?.map((ticket, index) => (
          <VehicleCard
            key={index}
            data={getCardData(ticket)}
            id={ticket.id}
            title={ticket.vehicle_board}
            subtitle={`${ticket.vehicle_model} ${ticket.vehicle_color}`}
            seeMore={() => [setShowTicket(true), setTicket(ticket)]}
            urlEdit={'/gestor-entregas/veiculos/multas/editar'}
          />
        ))}
      </Row>
      <Modal centered size="lg" show={showTicket} onHide={() => setShowTicket(false)}>
        <Modal.Header closeButton />
        <Modal.Body style={{ marginLeft: 20, marginRight: 20 }}>
          <Row style={{ marginBottom: 15 }}>
            <span className={styles.infoSpan}>
              <strong>Veículo:</strong> {`${ticket?.vehicle_model} ${ticket?.vehicle_color}`}
            </span>
            <span className={styles.infoSpan}>
              <strong>Placa:</strong> {ticket?.vehicle_board}
            </span>
            <span className={styles.infoSpan}>
              <strong>Motorista: </strong> {ticket?.driver_name}
            </span>
            <span className={styles.infoSpan}>
              <strong>Data: </strong> {formatDateTSX(ticket?.date, "dd/MM/yyyy 'às' HH:mm:ss")}
            </span>
            <span className={styles.infoSpan}>
              <strong>Tipo: </strong> {ticket?.type}
            </span>
            <span className={styles.infoSpan}>
              <strong>Custo: </strong> {formatMoney(ticket?.cost)}
            </span>
            <span className={styles.infoSpan}>
              <strong>Descrição: </strong>{' '}
              {ticket?.description ? ticket?.description : 'Não informada'}
            </span>
            <span className={styles.infoSpan}>
              <strong>Pontos: </strong> {ticket?.points}
            </span>
          </Row>
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  )
}

export default Multas
